import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import { ConfirmModal } from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import CompanyEditDialog from './CompanyEditDialog';
import { useServerTable } from '../../components/ui/DataTable';
import { adminApi } from '../../config/axios';
import { useDocumentTitle, formatSize } from '../../config/utils';
import avatar from '../../assets/img/unknown_person.jpg';

// (Retired: a 25-entry hardcoded TIMEZONES array behind a native <select>.
// A <select> can't display a value it has no <option> for — it reports "" —
// and this form read that value straight off the DOM via register(), so saving
// any unrelated field on a company whose zone wasn't listed silently WIPED the
// timezone. Replaced by the shared full-IANA TimezoneSelect, wired through
// Controller so the value lives in React state, not the DOM.)


export default function CompaniesList() {
  useDocumentTitle('Companies');
  const navigate       = useNavigate();
  const [search, setSearch]   = useState('');
  const [kind, setKind]       = useState('');   // '' = all, 'test' | 'customer'
  const [editItem, setEditItem] = useState(null);

  const fetcher = useCallback(async ({ page, perPage, sortField, sortOrder }) => {
    const res = await adminApi.post(`/datatable/companies/view?page=${page + 1}&per_page=${perPage}`, {
      order_by: { [sortField]: sortOrder },
      filters: {
        name: { value: search, matchMode: 'contains' },
        ...(kind ? { account_kind: { value: kind, matchMode: 'equals' } } : {}),
      },
    });
    return res.data;
  }, [search, kind]);

  const table = useServerTable(fetcher);

  const openEdit = (company) => setEditItem(company);

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Companies</h1>
          <p className="text-text-muted text-sm mt-0.5">{table.total} total clients</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-5">
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => { setSearch(e.target.value); table.setPage(0); }}
          className="max-w-sm"
          prefix={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
        />
      </div>

      <div className="mb-5 flex gap-2" dir="rtl" data-testid="kind-filter">
        {[['', 'الكل'], ['customer', 'زبون'], ['test', 'تجريبي']].map(([v, l]) => (
          <button key={v} type="button" aria-pressed={kind === v}
            className={kind === v ? 'btn-primary py-1 px-3 text-xs' : 'btn-secondary py-1 px-3 text-xs'}
            onClick={() => { setKind(v); table.setPage(0); }}>{l}</button>
        ))}
      </div>

      {/* Cards grid */}
      {table.loading && table.data.length === 0 ? (
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            {table.data.map(company => (
              <CompanyCard
                key={company.id}
                company={company}
                onView={() => navigate(`/companies/${company.id}`)}
                onEdit={() => openEdit(company)}
              />
            ))}
            {!table.loading && table.data.length === 0 && (
              <div className="col-span-full text-center py-16 text-text-muted">No companies found.</div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>
              Showing {table.page * table.perPage + 1}–{Math.min((table.page + 1) * table.perPage, table.total)} of {table.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={table.page === 0}
                onClick={() => table.setPage(table.page - 1)}
                className="btn-secondary py-1 px-3 disabled:opacity-40"
              >Previous</button>
              <button
                disabled={(table.page + 1) * table.perPage >= table.total}
                onClick={() => table.setPage(table.page + 1)}
                className="btn-secondary py-1 px-3 disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        </>
      )}

      {/* The same dialog the company detail page opens */}
      <CompanyEditDialog company={editItem} onClose={() => setEditItem(null)} onSaved={table.refresh} />
    </Layout>
  );
}

function CompanyCard({ company, onView, onEdit }) {
  return (
    <div className="card-padded flex flex-col gap-4 hover:glow-accent transition-all duration-200">
      <div className="flex items-center gap-3">
        <img
          src={company.logo ?? avatar}
          alt={company.name}
          className="w-12 h-12 rounded-xl object-cover border border-white/[0.08] flex-shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-text-main truncate">{company.name}</p>
          <p className="text-xs text-text-muted truncate">{company.email}</p>
        </div>
        {company.account_kind === 'test' && <Badge variant="warning">تجريبي</Badge>}
        <Badge variant={company.status === 'ACTIVE' ? 'teal' : 'danger'}>{company.status?.toLowerCase()}</Badge>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <Metric label="Devices" value={company.allowed_device_number} />
        <Metric label="Storage" value={formatSize(company.allowed_media_usage)} />
        <Metric label="Used" value={formatSize(company.total_media_usage)} />
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={onView} className="flex-1 btn-primary justify-center py-1.5 text-xs">View Profile</button>
        <button onClick={onEdit} className="btn-secondary py-1.5 px-3 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          <span className="text-xs">تعديل</span>
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white/[0.03] rounded-lg p-2 text-center">
      <p className="text-text-muted text-[10px] mb-0.5">{label}</p>
      <p className="text-text-main font-medium text-xs">{value}</p>
    </div>
  );
}
